import { useState, FormEvent, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ShieldCheck, CreditCard } from "lucide-react";
import { Link } from "react-router-dom";
import { useCart } from "@/contexts/CartContext";
import { ParticleBackground } from "@/components/ParticleBackground";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import { SEOHead } from "@/components/seo/SEOHead";
import { BreadcrumbSchema } from "@/components/seo/StructuredData";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { supabase } from "@/integrations/supabase/client";
import { getStripe } from "@/lib/stripe";
import { trackBeginCheckout } from "@/utils/analytics";
import { PaymentMethodIcons } from "@/components/PaymentMethodIcons";

// Stripe promise will be created inside component to ensure fresh env vars

interface CheckoutPayload {
  customer: {
    email: string;
    phone?: string;
  };
  delivery: {
    fullName: string;
    organisation?: string;
    addressLine1: string;
    addressLine2?: string;
    city: string;
    postcode: string;
    country: string;
  };
  notes?: string;
  cart: Array<{ id: string; name: string; quantity: number; price: number }>;
  totals: {
    subtotal: number;
    shipping: string;
    total: number;
  };
}

const PaymentForm = ({ 
  clientSecret, 
  onSuccess 
}: { 
  clientSecret: string; 
  onSuccess: (paymentIntentId: string) => void;
}) => {
  console.log('💳 STRIPE CHECKOUT: PaymentForm component rendering');
  console.log('🔑 STRIPE CHECKOUT: clientSecret prop:', clientSecret.slice(0, 30) + '...');
  
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);

  // Track when PaymentElement is mounted and ready
  useEffect(() => {
    console.log('🔍 STRIPE CHECKOUT: PaymentForm useEffect - checking elements...');
    console.log('🔍 STRIPE CHECKOUT: elements exists:', !!elements);
    
    if (elements) {
      const paymentElement = elements.getElement('payment');
      console.log('🔍 STRIPE CHECKOUT: paymentElement found:', !!paymentElement);
      
      if (paymentElement) {
        console.log('✅ STRIPE CHECKOUT: PaymentElement is mounted and ready');
        setIsReady(true);
      } else {
        console.log('⏳ STRIPE CHECKOUT: Waiting for PaymentElement to mount...');
        // Check again after a short delay
        const timer = setTimeout(() => {
          const element = elements.getElement('payment');
          if (element) {
            console.log('✅ STRIPE CHECKOUT: PaymentElement mounted after delay');
            setIsReady(true);
          } else {
            console.error('❌ STRIPE CHECKOUT: PaymentElement failed to mount after delay');
          }
        }, 1000);
        return () => clearTimeout(timer);
      }
    } else {
      console.log('⏳ STRIPE CHECKOUT: Elements not yet available');
    }
  }, [elements]);

  const handlePaymentSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    console.log('💳 STRIPE CHECKOUT: Payment submission started');
    console.log('🔍 STRIPE CHECKOUT: Stripe ready:', !!stripe);
    console.log('🔍 STRIPE CHECKOUT: Elements ready:', !!elements);
    
    if (!stripe || !elements) {
      console.error('❌ STRIPE CHECKOUT: Stripe or Elements not initialized');
      setPaymentError("Payment system not ready. Please wait a moment and try again.");
      return;
    }

    // Check if PaymentElement is actually mounted
    const paymentElement = elements.getElement('payment');
    console.log('🔍 STRIPE CHECKOUT: PaymentElement found:', !!paymentElement);
    
    if (!paymentElement) {
      console.error('❌ STRIPE CHECKOUT: PaymentElement not mounted');
      setPaymentError("Payment form not ready. Please wait for the payment form to load completely.");
      return;
    }

    setIsProcessing(true);
    setPaymentError(null);

    try {
      console.log('🚀 STRIPE CHECKOUT: Calling stripe.confirmPayment...');
      
      // Use redirect: "if_required" to handle the result programmatically
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: "if_required",
        confirmParams: {
          return_url: `${window.location.origin}/checkout/success`,
        },
      });

      console.log('📡 STRIPE CHECKOUT: confirmPayment result received');
      console.log('🔍 STRIPE CHECKOUT: error:', error);
      console.log('🔍 STRIPE CHECKOUT: paymentIntent status:', paymentIntent?.status);

      if (error) {
        // Payment failed - show error
        console.error('❌ STRIPE CHECKOUT: Payment confirmation failed:', error);
        const errorMsg = error.message || "There was an error processing your payment. Please try again.";
        setPaymentError(errorMsg);
        setIsProcessing(false);
      } else if (paymentIntent && paymentIntent.status === "succeeded") {
        // Payment succeeded - call success callback immediately with paymentIntent ID
        console.log('✅ STRIPE CHECKOUT: Payment succeeded! Calling onSuccess...');
        onSuccess(paymentIntent.id);
      } else if (paymentIntent && paymentIntent.status === "requires_action") {
        // 3DS authentication or other action needed - Stripe will handle it
        console.log('⚠️ STRIPE CHECKOUT: Payment requires additional action');
        setIsProcessing(false);
      } else {
        // Unexpected state
        console.error('❌ STRIPE CHECKOUT: Unexpected payment state:', paymentIntent?.status);
        setPaymentError("Payment status unclear. Please contact us at samboards19@aol.com to verify your order.");
        setIsProcessing(false);
      }
    } catch (error) {
      console.error('❌ STRIPE CHECKOUT: Payment exception:', error);
      const errorMessage = error instanceof Error ? error.message : "An unexpected error occurred";
      setPaymentError(`${errorMessage}. If this continues, please contact samboards19@aol.com`);
      setIsProcessing(false);
    }
  };

  console.log('🎨 STRIPE CHECKOUT: Rendering PaymentForm JSX');
  
  return (
    <form onSubmit={handlePaymentSubmit} className="space-y-4">
      {paymentError && (
        <div className="rounded-xl border border-red-500/50 bg-red-900/20 p-4">
          <p className="text-sm text-red-200">{paymentError}</p>
        </div>
      )}
      
      <div className="rounded-xl border border-slate-700/60 bg-slate-800/50 p-4">
        {(() => {
          console.log('🎨 STRIPE CHECKOUT: Rendering PaymentElement component');
          return (
            <PaymentElement 
              options={{ 
                layout: "tabs",
                wallets: {
                  applePay: 'auto',
                  googlePay: 'auto',
                },
                fields: {
                  billingDetails: {
                    address: 'auto',
                  },
                },
              }}
              onReady={() => {
                console.log('✅ STRIPE CHECKOUT: PaymentElement onReady fired');
                setIsReady(true);
              }}
              onLoadError={(event) => {
            console.error('❌ STRIPE CHECKOUT: PaymentElement load error');
            console.error('❌ STRIPE CHECKOUT: Error type:', event.error?.type);
            console.error('❌ STRIPE CHECKOUT: Error message:', event.error?.message);
            console.error('❌ STRIPE CHECKOUT: Full error:', JSON.stringify(event, null, 2));
            
            const errorMsg = event.error?.message || 'Failed to load payment form';
            
            // Show detailed error to help diagnose key mismatch
            let userMessage = errorMsg;
            if (errorMsg.includes('Invalid API Key') || errorMsg.includes('api key')) {
              userMessage = 'Payment system configuration error. Please contact us at samboards19@aol.com';
              console.error('🔑 STRIPE CHECKOUT: API key validation failed. This usually means:');
              console.error('   1. Publishable key does not match the account that created the PaymentIntent');
              console.error('   2. Publishable key has domain restrictions');
              console.error('   3. Keys are from different Stripe accounts (test vs live)');
            }
            
            toast({
              title: "Payment form error",
              description: userMessage,
              variant: "destructive",
            });
          }}
        />
          );
        })()}
      </div>
      {!isReady && (
        <p className="text-sm text-slate-400 text-center">Loading payment form...</p>
      )}
      <Button
        type="submit"
        size="lg"
        disabled={!stripe || !elements || !isReady || isProcessing}
        className="w-full"
      >
        {isProcessing ? "Processing payment..." : isReady ? "Pay now" : "Loading..."}
      </Button>
    </form>
  );
};

const Checkout = () => {
  const navigate = useNavigate();
  const { clearCart } = useCart();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [step, setStep] = useState<'details' | 'payment'>('details');
  const [stripePromise, setStripePromise] = useState<ReturnType<typeof getStripe> | null>(null);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  
  // Initialize Stripe on mount to get fresh environment variables
  useEffect(() => {
    console.log('🔐 STRIPE CHECKOUT: Initializing Stripe.js...');
    const stripe = getStripe();
    if (!stripe) {
      console.error('❌ STRIPE CHECKOUT: Failed to initialize Stripe');
      const errorMsg = "Unable to load payment system. Please refresh the page.";
      setPaymentError(errorMsg);
      toast({
        title: "Payment system error",
        description: errorMsg,
        variant: "destructive",
      });
    } else {
      console.log('✅ STRIPE CHECKOUT: Stripe.js initialized successfully');
      setStripePromise(stripe);
      setPaymentError(null);
    }
  }, []);
  
  // Read from localStorage
  const storedCart = JSON.parse(localStorage.getItem("cart") || "[]");
  const items = storedCart;
  const totalPrice = items.reduce((sum: number, item: any) => sum + item.price * item.quantity, 0);
  
  // Track begin checkout when cart is available
  useEffect(() => {
    if (items.length > 0 && step === 'details') {
      trackBeginCheckout(
        totalPrice,
        items.map((item: any) => ({
          item_id: item.id,
          item_name: item.name,
          price: item.price,
          quantity: item.quantity,
        }))
      );
    }
  }, [items.length, step]); // Only track when entering details step

  const [formData, setFormData] = useState({
    email: "",
    phone: "",
    fullName: "",
    organisation: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    postcode: "",
    country: "United Kingdom",
    notes: "",
    paymentMethod: "card",
  });

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }

    if (!formData.fullName.trim()) newErrors.fullName = "Full name is required";
    if (!formData.addressLine1.trim()) newErrors.addressLine1 = "Address is required";
    if (!formData.city.trim()) newErrors.city = "City/Town is required";
    if (!formData.postcode.trim()) newErrors.postcode = "Postcode is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  async function createPaymentIntent(payload: CheckoutPayload): Promise<string | null> {
    try {
      console.log('🔄 STRIPE CHECKOUT: calling create-payment-intent...');
      console.log('📦 STRIPE CHECKOUT: Order details:', {
        amount: payload.totals.total,
        itemCount: payload.cart.length,
        email: payload.customer.email
      });
      console.log('🌐 STRIPE CHECKOUT: fetch URL = supabase.functions.invoke("create-payment-intent")');

      const { data, error } = await supabase.functions.invoke('create-payment-intent', {
        body: {
          amount: payload.totals.total,
          currency: 'gbp',
          customerEmail: payload.customer.email,
          customerName: payload.delivery.fullName,
          deliveryAddress: payload.delivery,
          items: payload.cart,
        },
      });

      console.log('📡 STRIPE CHECKOUT: response received from edge function');
      console.log('📋 STRIPE CHECKOUT: response JSON =', { data, error });

      if (error) {
        console.error('❌ STRIPE CHECKOUT ERROR: create-payment-intent failed with error:', error);
        throw new Error(error.message || 'Payment server error');
      }
      
      if (!data) {
        console.error('❌ STRIPE CHECKOUT ERROR: create-payment-intent returned no data');
        throw new Error('No response from payment server');
      }

      if (!data.clientSecret) {
        console.error('❌ STRIPE CHECKOUT ERROR: create-payment-intent missing clientSecret:', data);
        throw new Error('Invalid payment response');
      }

      console.log('✅ STRIPE CHECKOUT: PaymentIntent created successfully');
      console.log('🔑 STRIPE CHECKOUT: clientSecret received:', data.clientSecret.slice(0, 30) + '...');
      
      return data.clientSecret;
    } catch (error) {
      console.error('❌ STRIPE CHECKOUT ERROR:', error instanceof Error ? error.message : String(error));
      const errorMessage = error instanceof Error ? error.message : "Unable to initialize payment. Please try again.";
      toast({
        title: "Payment setup failed",
        description: `${errorMessage}. Please contact samboards19@aol.com for assistance.`,
        variant: "destructive",
      });
      return null;
    }
  }

  const handleContinueToPayment = async () => {
    console.log('🎬 STRIPE CHECKOUT: handleContinueToPayment called');
    
    // Check if payment system is ready
    if (paymentError || !stripePromise) {
      console.error('❌ STRIPE CHECKOUT ERROR: Payment system not initialized');
      toast({
        title: "Payment system unavailable",
        description: "Please refresh the page and try again.",
        variant: "destructive",
      });
      return;
    }
    
    if (items.length === 0) {
      console.error('❌ STRIPE CHECKOUT ERROR: Cart is empty');
      toast({
        title: "Cart is empty",
        description: "Please add items to your cart before checking out.",
        variant: "destructive",
      });
      return;
    }

    if (!validateForm()) {
      console.log('❌ STRIPE CHECKOUT: Form validation failed');
      const firstError = Object.keys(errors)[0];
      document.getElementById(firstError)?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    setIsSubmitting(true);

    try {
      const payload: CheckoutPayload = {
        customer: {
          email: formData.email,
          phone: formData.phone || undefined,
        },
        delivery: {
          fullName: formData.fullName,
          organisation: formData.organisation || undefined,
          addressLine1: formData.addressLine1,
          addressLine2: formData.addressLine2 || undefined,
          city: formData.city,
          postcode: formData.postcode,
          country: formData.country,
        },
        notes: formData.notes || undefined,
        cart: items.map(item => ({
          id: item.id,
          name: item.name,
          quantity: item.quantity,
          price: item.price,
        })),
        totals: {
          subtotal: totalPrice,
          shipping: "Calculated at checkout",
          total: totalPrice,
        },
      };

      const secret = await createPaymentIntent(payload);
      console.log('🔍 STRIPE CHECKOUT: createPaymentIntent returned:', !!secret);
      
      if (secret) {
        console.log('✅ STRIPE CHECKOUT: clientSecret state set to:', secret.slice(0, 30) + '...');
        setClientSecret(secret);
        setPaymentError(null);
        console.log('🎯 STRIPE CHECKOUT: Transitioning to payment step');
        setStep('payment');
        console.log('✅ STRIPE CHECKOUT: Step changed to "payment"');
      } else {
        console.error('❌ STRIPE CHECKOUT ERROR: Failed to get client secret');
        setPaymentError("Payment system configuration error. Please try again later.");
      }
    } catch (error) {
      console.error('❌ STRIPE CHECKOUT ERROR:', error instanceof Error ? error.message : String(error));
      setPaymentError("An unexpected error occurred. Please try again.");
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePaymentSuccess = async (paymentIntentId: string) => {
    try {
      // Generate order number
      const orderNumber = `ORD-${Date.now()}-${Math.random().toString(36).substr(2, 9).toUpperCase()}`;
      
      // Save order to database
      const { error: dbError } = await supabase.from('orders').insert({
        order_number: orderNumber,
        customer_email: formData.email,
        customer_name: formData.fullName,
        customer_phone: formData.phone,
        delivery_address: {
          fullName: formData.fullName,
          organisation: formData.organisation,
          addressLine1: formData.addressLine1,
          addressLine2: formData.addressLine2,
          city: formData.city,
          postcode: formData.postcode,
          country: formData.country,
        },
        items: items,
        subtotal: totalPrice,
        shipping: 0,
        total: totalPrice,
        payment_intent_id: paymentIntentId,
        status: 'processing',
      });

      if (dbError) {
        console.error('Error saving order to database:', dbError);
      }

      // Send order confirmation email
      sendOrderConfirmation();
      
      // Clear cart
      localStorage.removeItem("cart");
      clearCart();
      
      // Navigate to success page with payment_intent
      navigate(`/checkout/success?payment_intent=${paymentIntentId}`);
    } catch (error) {
      console.error('Error processing order:', error);
      // Still navigate to success even if database save fails
      sendOrderConfirmation();
      localStorage.removeItem("cart");
      clearCart();
      navigate(`/checkout/success?payment_intent=${paymentIntentId}`);
    }
  };

  const sendOrderConfirmation = async () => {
    try {
      // Store order data in localStorage for success page
      localStorage.setItem("last_order", JSON.stringify({
        customerEmail: formData.email,
        customerName: formData.fullName,
        orderTotal: totalPrice,
        items: items.map(item => ({
          name: item.name,
          quantity: item.quantity,
          price: item.price,
        })),
      }));
      
      await supabase.functions.invoke('send-order-confirmation', {
        body: {
          customerEmail: formData.email,
          customerName: formData.fullName,
          orderTotal: totalPrice,
          items: items.map(item => ({
            name: item.name,
            quantity: item.quantity,
            price: item.price,
          })),
          deliveryAddress: {
            fullName: formData.fullName,
            organisation: formData.organisation,
            addressLine1: formData.addressLine1,
            addressLine2: formData.addressLine2,
            city: formData.city,
            postcode: formData.postcode,
            country: formData.country,
          },
        },
      });
      console.log('Order confirmation email sent');
    } catch (error) {
      console.error('Failed to send confirmation email:', error);
      // Don't block the success flow if email fails
    }
  };

  const updateField = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#020817] via-[#04151F] to-[#020817] text-slate-50">
        <ParticleBackground />
        <div className="relative w-full max-w-7xl mx-auto px-4 md:px-8 z-10" style={{ paddingTop: 'var(--nav-height)' }}>
          <div className="py-16 text-center">
            <h1 className="text-3xl font-semibold mb-4">Your cart is empty</h1>
            <p className="text-slate-300 mb-8">Add some products to get started.</p>
            <Button asChild>
              <Link to="/shop">Back to shop</Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEOHead
        title="Secure Checkout | Samboards"
        description="Complete your order for premium tactile sensory tools. Secure checkout with multiple payment options. UK delivery available."
        canonical="https://samboards.co.uk/checkout"
        keywords="Samboards checkout, secure payment, UK delivery, sensory tools order"
      />
      <BreadcrumbSchema
        items={[
          { name: "Home", url: "/" },
          { name: "Shop", url: "/shop" },
          { name: "Checkout", url: "/checkout" }
        ]}
      />
      
      <div className="min-h-screen bg-gradient-to-b from-[#020817] via-[#04151F] to-[#020817] text-slate-50">
        <ParticleBackground />
      <div className="relative w-full max-w-7xl mx-auto px-4 md:px-8 z-10 py-8" style={{ paddingTop: 'var(--nav-height)' }}>
        <Link
          to="/shop"
          className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-emerald-300 mb-6 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to shop
        </Link>

        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">Secure checkout</h1>
          <p className="mt-2 text-slate-300">Review your order and complete payment.</p>
        </div>

        <div className="grid gap-6 sm:gap-8 lg:grid-cols-[1fr,400px]">
          {/* Left Column - Forms */}
          <div className="space-y-4 sm:space-y-6">
            {step === 'details' && (
              <>
                {/* Contact Details */}
                <div className="rounded-2xl border border-slate-700/60 bg-slate-900/70 p-6 shadow-lg backdrop-blur-md">
                <h2 className="text-xl font-semibold mb-4">Contact details</h2>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="email" className="text-slate-200">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => updateField("email", e.target.value)}
                      className={`mt-1.5 bg-slate-800/50 border-slate-700 text-slate-100 ${errors.email ? 'border-red-500' : ''}`}
                      placeholder="your@email.com"
                    />
                    {errors.email && <p className="text-red-400 text-sm mt-1">{errors.email}</p>}
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-slate-200">Phone (optional)</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => updateField("phone", e.target.value)}
                      className="mt-1.5 bg-slate-800/50 border-slate-700 text-slate-100"
                      placeholder="+44"
                    />
                  </div>
                </div>
              </div>

              {/* Delivery Information */}
              <div className="rounded-2xl border border-slate-700/60 bg-slate-900/70 p-6 shadow-lg backdrop-blur-md">
                <h2 className="text-xl font-semibold mb-4">Delivery information</h2>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="fullName" className="text-slate-200">Full name *</Label>
                    <Input
                      id="fullName"
                      value={formData.fullName}
                      onChange={(e) => updateField("fullName", e.target.value)}
                      className={`mt-1.5 bg-slate-800/50 border-slate-700 text-slate-100 ${errors.fullName ? 'border-red-500' : ''}`}
                    />
                    {errors.fullName && <p className="text-red-400 text-sm mt-1">{errors.fullName}</p>}
                  </div>
                  <div>
                    <Label htmlFor="organisation" className="text-slate-200">Organisation / School (optional)</Label>
                    <Input
                      id="organisation"
                      value={formData.organisation}
                      onChange={(e) => updateField("organisation", e.target.value)}
                      className="mt-1.5 bg-slate-800/50 border-slate-700 text-slate-100"
                    />
                  </div>
                  <div>
                    <Label htmlFor="addressLine1" className="text-slate-200">Address line 1 *</Label>
                    <Input
                      id="addressLine1"
                      value={formData.addressLine1}
                      onChange={(e) => updateField("addressLine1", e.target.value)}
                      className={`mt-1.5 bg-slate-800/50 border-slate-700 text-slate-100 ${errors.addressLine1 ? 'border-red-500' : ''}`}
                    />
                    {errors.addressLine1 && <p className="text-red-400 text-sm mt-1">{errors.addressLine1}</p>}
                  </div>
                  <div>
                    <Label htmlFor="addressLine2" className="text-slate-200">Address line 2 (optional)</Label>
                    <Input
                      id="addressLine2"
                      value={formData.addressLine2}
                      onChange={(e) => updateField("addressLine2", e.target.value)}
                      className="mt-1.5 bg-slate-800/50 border-slate-700 text-slate-100"
                    />
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="city" className="text-slate-200">City/Town *</Label>
                      <Input
                        id="city"
                        value={formData.city}
                        onChange={(e) => updateField("city", e.target.value)}
                        className={`mt-1.5 bg-slate-800/50 border-slate-700 text-slate-100 ${errors.city ? 'border-red-500' : ''}`}
                      />
                      {errors.city && <p className="text-red-400 text-sm mt-1">{errors.city}</p>}
                    </div>
                    <div>
                      <Label htmlFor="postcode" className="text-slate-200">Postcode *</Label>
                      <Input
                        id="postcode"
                        value={formData.postcode}
                        onChange={(e) => updateField("postcode", e.target.value)}
                        className={`mt-1.5 bg-slate-800/50 border-slate-700 text-slate-100 ${errors.postcode ? 'border-red-500' : ''}`}
                      />
                      {errors.postcode && <p className="text-red-400 text-sm mt-1">{errors.postcode}</p>}
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="country" className="text-slate-200">Country</Label>
                    <Select value={formData.country} onValueChange={(value) => updateField("country", value)}>
                      <SelectTrigger className="mt-1.5 bg-slate-800/50 border-slate-700 text-slate-100">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="United Kingdom">United Kingdom</SelectItem>
                        <SelectItem value="Ireland">Ireland</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="notes" className="text-slate-200">Order notes (optional)</Label>
                    <Textarea
                      id="notes"
                      value={formData.notes}
                      onChange={(e) => updateField("notes", e.target.value)}
                      className="mt-1.5 bg-slate-800/50 border-slate-700 text-slate-100"
                      placeholder="Any special instructions for your order"
                      rows={3}
                    />
                  </div>
                </div>
              </div>

                {/* Payment Preview */}
                <div className="rounded-2xl border border-slate-700/60 bg-slate-900/70 p-6 shadow-lg backdrop-blur-md">
                  <h2 className="text-xl font-semibold mb-4">Payment</h2>
                  <div className="flex items-start gap-3 p-4 rounded-xl border-2 border-emerald-500/40 bg-emerald-500/5">
                    <CreditCard className="h-5 w-5 mt-0.5 text-emerald-400" />
                    <div className="flex-1">
                      <div className="font-medium text-slate-100">Secure Payment</div>
                      <p className="text-sm text-slate-300 mt-1">
                        Supports: Card • Apple Pay • Google Pay • Link • Klarna • Clearpay • PayPal
                      </p>
                      <p className="text-xs text-slate-400 mt-2">
                        All payments processed securely by Stripe. No card data stored on this site.
                      </p>
                    </div>
                  </div>
                </div>

                <Button
                  type="button"
                  size="lg"
                  onClick={handleContinueToPayment}
                  disabled={isSubmitting || items.length === 0 || !!paymentError || !stripePromise}
                  className="w-full"
                >
                  {isSubmitting ? "Setting up payment..." : "Continue to payment"}
                </Button>
                {paymentError && (
                  <p className="text-red-400 text-sm text-center mt-2">
                    Payment system unavailable. Please refresh the page.
                  </p>
                )}
              </>
            )}

            {step === 'payment' && (
                <div className="rounded-2xl border border-slate-700/60 bg-slate-900/70 p-6 shadow-lg backdrop-blur-md">
                  <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
                    <ShieldCheck className="h-5 w-5 text-emerald-400" />
                    Complete payment
                  </h2>
                  
                  {/* Payment Method Icons */}
                  <div className="mb-6">
                    <PaymentMethodIcons />
                  </div>
                  
                  {(() => {
                    console.log('🎨 STRIPE CHECKOUT: Rendering payment step');
                    console.log('🔍 STRIPE CHECKOUT: stripePromise exists:', !!stripePromise);
                    console.log('🔍 STRIPE CHECKOUT: clientSecret exists:', !!clientSecret);
                    console.log('🔍 STRIPE CHECKOUT: paymentError:', paymentError);
                    
                    // Show error if payment system failed to initialize or payment intent creation failed
                    if (paymentError) {
                      console.error('❌ STRIPE CHECKOUT: Showing payment error:', paymentError);
                      return (
                        <div className="text-center py-8 space-y-4">
                          <div className="text-red-400 text-lg font-medium">Payment system error</div>
                          <p className="text-slate-400">{paymentError}</p>
                          <Button
                            onClick={() => window.location.reload()}
                            variant="outline"
                            className="mt-4"
                          >
                            Refresh page
                          </Button>
                        </div>
                      );
                    }
                    
                    if (!stripePromise) {
                      console.log('⏳ STRIPE CHECKOUT: Waiting for Stripe.js to initialize...');
                      return (
                        <div className="text-center py-8">
                          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-400 mb-4"></div>
                          <p className="text-slate-400">Initializing payment system...</p>
                        </div>
                      );
                    }
                    
                    if (!clientSecret) {
                      console.log('⏳ STRIPE CHECKOUT: Waiting for clientSecret...');
                      return (
                        <div className="text-center py-8">
                          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-400 mb-4"></div>
                          <p className="text-slate-400">Loading payment form...</p>
                        </div>
                      );
                    }
                    
                    console.log('✅ STRIPE CHECKOUT: rendering <Elements> wrapper...');
                    console.log('🔑 STRIPE CHECKOUT: clientSecret =', clientSecret.slice(0, 30) + '...');
                    
                    return (
                      <Elements 
                        stripe={stripePromise} 
                        options={{ 
                          clientSecret,
                          appearance: {
                            theme: 'night',
                            variables: {
                              colorPrimary: '#6EEDC1',
                              colorBackground: '#1e293b',
                              colorText: '#f1f5f9',
                              colorDanger: '#ef4444',
                              fontFamily: 'system-ui, sans-serif',
                              spacingUnit: '4px',
                              borderRadius: '8px',
                            }
                          }
                        }}
                      >
                        <PaymentForm clientSecret={clientSecret} onSuccess={handlePaymentSuccess} />
                      </Elements>
                    );
                  })()}
              </div>
            )}
          </div>

          {/* Right Column - Order Summary */}
            <div className="lg:sticky lg:top-24 h-fit">
              <div className="rounded-2xl border border-slate-700/60 bg-slate-900/70 p-6 shadow-lg backdrop-blur-md">
                <h2 className="text-xl font-semibold mb-4">Order summary</h2>
                
                <div className="space-y-4 mb-6">
                  {items.map((item) => (
                    <div key={item.id} className="flex justify-between text-sm">
                      <div className="flex-1">
                        <p className="text-slate-100 font-medium">{item.name}</p>
                        <p className="text-slate-400">Qty: {item.quantity}</p>
                      </div>
                      <p className="text-slate-100 font-medium">
                        £{(item.price * item.quantity).toFixed(2)}
                      </p>
                    </div>
                  ))}
                </div>

                <div className="border-t border-slate-700/60 pt-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-300">Subtotal</span>
                    <span className="text-slate-100">£{totalPrice.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-300">Shipping</span>
                    <span className="text-slate-400 italic">Calculated at checkout</span>
                  </div>
                  <div className="flex justify-between text-lg font-semibold pt-2 border-t border-slate-700/60">
                    <span className="text-slate-100">Total</span>
                    <span className="text-emerald-400">£{totalPrice.toFixed(2)}</span>
                  </div>
                </div>

                <div className="mt-6 flex items-start gap-2 text-xs text-slate-400">
                  <ShieldCheck className="h-4 w-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <p>
                    Your payment information is processed securely. We do not store credit card details.
                  </p>
                </div>
                
                {/* Payment Method Icons in Sidebar */}
                <div className="mt-6 pt-6 border-t border-slate-700/60">
                  <PaymentMethodIcons />
                </div>
              </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default Checkout;
